export interface ChatFilePart {
  type: 'file';
  url: string;
  mediaType: string;
  filename: string;
  /** Stable browser-reachable URL for UI rendering (never a data: URL). */
  displayUrl?: string;
  imageId?: string;
}

interface CompletedUploadFile {
  file: File;
  publicUrl?: string;
  imageId?: string;
}

interface AttachmentLike {
  url?: string;
  displayUrl?: string;
  imageId?: string;
}

interface ImageWithUrls {
  id: string | number;
  optimizedUrl?: string;
  thumbnailUrl?: string;
}

export function completedFilesToFileParts(completedFiles: CompletedUploadFile[]): ChatFilePart[] {
  return completedFiles
    .filter((f) => Boolean(f.publicUrl) && !f.publicUrl!.startsWith('data:'))
    .map((f) => {
      const publicUrl = f.publicUrl!;
      return {
        type: 'file' as const,
        url: publicUrl,
        displayUrl: publicUrl,
        mediaType: f.file.type,
        filename: f.file.name,
        ...(f.imageId != null && { imageId: String(f.imageId) }),
      };
    });
}

/** Prefer a stable HTTP(S) URL for thumbnails; never surface inlined data: URLs in the UI. */
export function resolveAttachmentDisplayUrl(attachment: AttachmentLike, images?: ImageWithUrls[]): string {
  if (attachment.displayUrl && !attachment.displayUrl.startsWith('data:')) {
    return attachment.displayUrl;
  }

  if (attachment.url && !attachment.url.startsWith('data:')) {
    return attachment.url;
  }

  if (attachment.imageId != null && images?.length) {
    const image = images.find((img) => String(img.id) === String(attachment.imageId));
    if (image?.optimizedUrl) return image.optimizedUrl;
    if (image?.thumbnailUrl) return image.thumbnailUrl;
  }

  return '';
}

/** Re-apply stable HTTP(S) URLs on the optimistic user message after sendMessage. */
export function applyStableFilePartUrls<T extends { role?: string; parts?: any[] }>(
  messages: T[],
  sentFiles: ChatFilePart[]
): T[] {
  if (!sentFiles.length || !messages.length) return messages;

  const lastIdx = messages.length - 1;
  const last = messages[lastIdx];
  if (last.role !== 'user' || !last.parts?.length) return messages;

  let fileIndex = 0;
  const updatedParts = last.parts.map((part) => {
    if (part.type !== 'file') return part;

    const sent = sentFiles[fileIndex] ?? sentFiles[sentFiles.length - 1];
    fileIndex += 1;
    const displayUrl = sent.displayUrl ?? sent.url;

    return {
      ...part,
      url: displayUrl,
      displayUrl,
      mediaType: part.mediaType ?? sent.mediaType,
      filename: part.filename ?? sent.filename,
      ...(sent.imageId != null && { imageId: sent.imageId }),
    };
  });

  const updated = [...messages];
  updated[lastIdx] = { ...last, parts: updatedParts };
  return updated;
}
